import { useMemo } from "react";
import Customer from "../../../models/Customer";
import { useContracts } from "../../../networking/contract.hooks";
import { useAllCustomers } from "../../../networking/customer.hooks";
import { useOrganisation } from "../../../networking/organisation.hooks";
import { useSubsidiaries } from "../../../networking/subsidiary.hooks";

export interface CustomerFilterOptions {
	pools: { id: string; name: string }[];
	contracts: { id: string; name: string }[];
	/** org `customerTagTypes` — descriptive customer tags (key + resolved Swedish name) */
	tags: { id: string; name: string }[];
	/** the org's subsidiaries; the filter section is hidden when the org has none */
	subsidiaries: { id: string; name: string }[];
}

/**
 * Bundles everything the customer filter UI needs: the full customer list and
 * the distinct filterable values (org units / pools / contracts) present in it.
 * The customer list carries only ids for pool and contract, so names are resolved
 * from the org's pool list and the contracts endpoint respectively. Shared by the
 * customer-list filter drawer and the org-defined-filters settings editor so both
 * offer the exact same options.
 */
export function useCustomerFilterData(pool: string = "") {
	const { customers, isLoading } = useAllCustomers(pool);
	const { data: organisation } = useOrganisation();
	// Straight from the org's subsidiaries (like the tag taxonomy) so the filter
	// can be built before any customer is connected to one.
	const { subsidiaries: subsidiaryList } = useSubsidiaries();
	// Contracts carry only an id on the customer; resolve names from the contract
	// list (large page so the whole set is available for labelling).
	const { contracts: contractList } = useContracts({ pageSize: 10000 });

	// The customer carries only the pool key; resolve names from the org's pools.
	const poolNameById = useMemo(() => {
		const map = new Map<string, string>();
		(organisation?.pools ?? []).forEach((p) => map.set(p.key, p.name || p.key));
		return map;
	}, [organisation]);

	const contractNameById = useMemo(() => {
		const map = new Map<string, string>();
		(contractList ?? []).forEach((c) => map.set(c.id, c.name || c.id));
		return map;
	}, [contractList]);

	const options = useMemo<CustomerFilterOptions>(() => {
		const poolKeys = new Set<string>();
		const contractIds = new Set<string>();

		for (const c of customers as Customer[]) {
			if (c.pool) poolKeys.add(c.pool);
			if (c.contract?.id) contractIds.add(c.contract.id);
		}

		// Straight from the org taxonomy (like the pool name resolution) so tags
		// are filterable before any customer carries them.
		const tags = (organisation?.customerTagTypes ?? [])
			.map((tag) => ({ id: tag.key, name: tag.name?.sv || tag.key }))
			.sort((a, b) => a.name.localeCompare(b.name, "sv"));

		return {
			pools: [...poolKeys]
				.map((id) => ({ id, name: poolNameById.get(id) || id }))
				.sort((a, b) => a.name.localeCompare(b.name, "sv")),
			contracts: [...contractIds]
				.map((id) => ({ id, name: contractNameById.get(id) || id }))
				.sort((a, b) => a.name.localeCompare(b.name, "sv")),
			tags,
			subsidiaries: subsidiaryList
				.map((s) => ({ id: s.id, name: s.name }))
				.sort((a, b) => a.name.localeCompare(b.name, "sv")),
		};
	}, [customers, poolNameById, contractNameById, organisation, subsidiaryList]);

	return { customers, options, poolNameById, contractNameById, isLoading };
}
